import { useState, useCallback, useRef, useEffect } from 'react';
import HubV2Layout from '@/components/hub-v2/HubV2Layout';
import { HubSavedSimulations } from '@/components/hub-v2/HubSavedSimulations';
import { useHubSimulations, type HubSimulation } from '@/hooks/useHubSimulations';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ArrowLeft, Save, GitBranch, Building2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useQuery } from '@tanstack/react-query';
import { CustomerSearchCombobox } from '@/components/crm/CustomerSearchCombobox';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import type { Organization } from '@/types/crm';

export default function CRMAnalysesHubV2() {
  const { simulations, isLoading, createSimulation, updateSimulation, deleteSimulation, createVersion } = useHubSimulations();
  const [selected, setSelected] = useState<HubSimulation | null>(null);
  const [editName, setEditName] = useState('');
  const currentParamsRef = useRef<Record<string, any>>({});
  const currentFinancialParamsRef = useRef<Record<string, any> | null>(null);



  // New simulation dialog
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [newName, setNewName] = useState('');
  const [newOrgId, setNewOrgId] = useState<string>('none');

  // Fetch organizations for combobox
  const { data: organizations = [] } = useQuery({
    queryKey: ['hub-organizations'],
    queryFn: async () => {
      const { data } = await supabase
        .from('organizations')
        .select('id, name, classification, status, website, favicon_url, logo_url')
        .is('deleted_at', null)
        .order('name');
      return (data ?? []) as Organization[];
    },
  });

  // Keep editName in sync with selected
  useEffect(() => {
    if (selected) setEditName(selected.name);
  }, [selected?.id]);

  const handleNew = useCallback(() => {
    setNewName(`Simulatie ${(simulations?.length ?? 0) + 1}`);
    setNewOrgId('none');
    setShowNewDialog(true);
  }, [simulations]);

  const handleCreateConfirm = useCallback(async () => {
    try {
      const result = await createSimulation.mutateAsync({
        name: newName || 'Nieuwe Simulatie',
        profile_id: 'wognum',
        sim_type: 'simulatie',
        sim_params: {},
        org_id: newOrgId !== 'none' ? newOrgId : null,
      });
      setSelected(result);
      setShowNewDialog(false);
    } catch {}
  }, [createSimulation, newName, newOrgId]);

  const handleSave = useCallback(async () => {
    if (!selected) return;
    try {
      const updated = await updateSimulation.mutateAsync({
        id: selected.id,
        name: editName || selected.name,
        sim_params: currentParamsRef.current,
        financial_params: currentFinancialParamsRef.current ?? undefined,
      });
      setSelected(updated);
    } catch {}
  }, [selected, updateSimulation, editName]);

  const handleSaveAsVersion = useCallback(async () => {
    if (!selected) return;
    try {
      // First save current state
      const currentSim: HubSimulation = {
        ...selected,
        name: editName || selected.name,
        sim_params: currentParamsRef.current,
        financial_params: currentFinancialParamsRef.current,
      };
      const newVersion = await createVersion.mutateAsync(currentSim);
      setSelected(newVersion);
    } catch {}
  }, [selected, createVersion, editName]);

  const handleDelete = useCallback(async (id: string) => {
    await deleteSimulation.mutateAsync(id);
    if (selected?.id === id) setSelected(null);
  }, [deleteSimulation, selected]);

  const handleView = useCallback((sim: HubSimulation) => {
    setSelected(sim);
  }, []);

  const handleDuplicate = useCallback(async (sim: HubSimulation) => {
    try {
      const result = await createSimulation.mutateAsync({
        name: `${sim.name} (kopie)`,
        profile_id: sim.profile_id,
        sim_type: sim.sim_type,
        sim_params: sim.sim_params,
        financial_params: sim.financial_params ?? undefined,
        org_id: sim.org_id,
      });
      setSelected(result);
    } catch {}
  }, [createSimulation]);

  const handleBack = useCallback(() => {
    setSelected(null);
  }, []);

  const handleParamsChange = useCallback((params: Record<string, any>) => {
    currentParamsRef.current = params;
  }, []);

  const handleFinancialParamsChange = useCallback((params: Record<string, any>) => {
    currentFinancialParamsRef.current = params;
  }, []);

  // Version history for current simulation
  const versionHistory = selected
    ? simulations?.filter(s =>
        s.id === selected.id ||
        s.parent_id === selected.id ||
        s.id === selected.parent_id ||
        (selected.parent_id && s.parent_id === selected.parent_id)
      ).sort((a, b) => a.version - b.version)
    : [];

  return (
    <>
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {selected && (
              <Button variant="ghost" size="icon" onClick={handleBack} className="shrink-0">
                <ArrowLeft className="h-4 w-4" />
              </Button>
            )}
            <div>
              {selected ? (
                <div className="flex items-center gap-3">
                  <Input
                    value={editName}
                    onChange={e => setEditName(e.target.value)}
                    className="text-xl font-bold h-9 border-transparent hover:border-border focus:border-border bg-transparent px-1 w-[300px]"
                  />
                  <Badge variant="outline" className="shrink-0">
                    <GitBranch className="h-3 w-3 mr-1" />
                    v{selected.version}
                  </Badge>
                  {selected.org_name && (
                    <Badge variant="secondary" className="shrink-0">
                      <Building2 className="h-3 w-3 mr-1" />
                      {selected.org_name}
                    </Badge>
                  )}
                </div>
              ) : (
                <h1 className="text-2xl font-bold text-foreground">HUB V2 Simulatie</h1>
              )}
              <p className="text-sm text-muted-foreground">
                {selected
                  ? 'Energiepark simulatie met PV, BESS, grid en financieel model'
                  : 'Beheer en vergelijk energiepark simulaties'}
              </p>
            </div>
          </div>
          {selected && (
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleSaveAsVersion}
                className="gap-2"
                disabled={createVersion.isPending}
              >
                <GitBranch className="h-4 w-4" />
                Nieuwe versie
              </Button>
              <Button size="sm" onClick={handleSave} className="gap-2" disabled={updateSimulation.isPending}>
                <Save className="h-4 w-4" />
                Opslaan
              </Button>
            </div>
          )}
        </div>

        {/* Version history bar */}
        {selected && versionHistory && versionHistory.length > 1 && (
          <div className="flex items-center gap-2 text-xs">
            <span className="text-muted-foreground font-medium">Versies:</span>
            {versionHistory.map(v => (
              <Button
                key={v.id}
                variant={v.id === selected.id ? 'default' : 'outline'}
                size="sm"
                className="h-6 text-xs px-2"
                onClick={() => setSelected(v)}
              >
                v{v.version}
              </Button>
            ))}
          </div>
        )}

        {!selected ? (
          <HubSavedSimulations
            simulations={simulations}
            isLoading={isLoading}
            onDelete={handleDelete}
            onView={handleView}
            onNew={handleNew}
            onDuplicate={handleDuplicate}
          />
        ) : (
          <HubV2Layout
            simulation={selected}
            onSave={handleSave}
            onParamsChange={handleParamsChange}
            onFinancialParamsChange={handleFinancialParamsChange}
            orgId={selected?.org_id}
          />
        )}
      </div>

      {/* New Simulation Dialog */}
      <Dialog open={showNewDialog} onOpenChange={setShowNewDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nieuwe HUB Simulatie</DialogTitle>
            <DialogDescription>Geef de simulatie een naam en koppel optioneel een klant.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Naam</Label>
              <Input
                value={newName}
                onChange={e => setNewName(e.target.value)}
                placeholder="Simulatie naam..."
                autoFocus
              />
            </div>
            <div className="space-y-2">
              <Label>Klant (optioneel)</Label>
              <CustomerSearchCombobox
                organizations={organizations}
                value={newOrgId}
                onValueChange={setNewOrgId}
                placeholder="Selecteer klant..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewDialog(false)}>Annuleren</Button>
            <Button onClick={handleCreateConfirm} disabled={createSimulation.isPending}>
              Aanmaken
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
